import { useCallback, useEffect, useRef } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { DoneButton } from "containers/Modal/styles";
import { setModal } from "state/config-slice";
import { offersState, selectOfferByName, selectOffersByPartner, selectOfferByDeepLink } from "state/offers-slice";
import Offer, { NotEligibleState, Status } from "types/Offer";

const useDeeplink = () => {
  const [cookies, , removeCookies] = useCookies();
  const offers = useSelector(offersState);
  const offersByPartner: Offer[] = useSelector(selectOffersByPartner(cookies.PARTNER));

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { search, state } = useLocation();
  const navigate = useNavigate();
  const hasRedirectedWithToken = useRef(false);

  const activeOffersByPartner = offersByPartner.filter(
    (offer: Offer) => offer.marketplaceInfo.status === Status.active,
  );

  const purchasableOffersByPartner = offersByPartner.filter(
    (offer: Offer) => offer.marketplaceInfo.status === Status.purchasable,
  );

  const pendingActivationOffersByPartner = offersByPartner.filter(
    (offer: Offer) => offer.marketplaceInfo.status === Status.pendingActivation,
  );

  const restOffersByPartner = offersByPartner.filter(
    (offer: Offer) =>
      offer.marketplaceInfo.status !== Status.active &&
      offer.marketplaceInfo.status !== Status.purchasable &&
      offer.marketplaceInfo.status !== Status.pendingActivation,
  );

  const offersByPartnerSortedByPriority: Offer[] = [
    ...activeOffersByPartner,
    ...purchasableOffersByPartner,
    ...pendingActivationOffersByPartner,
    ...restOffersByPartner,
  ];

  const selectedOfferToDeeplinkWithPartner: Offer = offersByPartnerSortedByPriority[0];
  const selectedOfferToDeeplinkWithToken = offers.offersList.length === 1 ? offers.offersList[0] : null;
  const hasDeeplinkToken = Boolean(
    new URLSearchParams(search).get("tk") || window.sessionStorage.getItem("deeplinkTk"),
  );

  const offerByName: Offer = useSelector(selectOfferByName(cookies.OFFER));
  const offerByDeepLink: Offer = useSelector(selectOfferByDeepLink(cookies.OFFER));

  const selectedOfferToDeeplinkWithName =
    offerByName ||
    (cookies.STATE === "account_already_exist" ? selectedOfferToDeeplinkWithPartner : undefined) ||
    offerByDeepLink;

  const openNotEligibleModal = useCallback(() => {
    const notEligible = selectedOfferToDeeplinkWithName?.marketplaceInfo?.notEligible;

    const state: NotEligibleState = cookies.STATE;
    const { heading, details } = notEligible?.[state] || {};

    dispatch(
      setModal({
        showModal: true,
        errorModal: true,
        size: "large",
        title: t(heading as string),
        description: t(details as string),
        customButtons: (
          <DoneButton
            title={t("Ok")}
            onClick={() => {
              dispatch(setModal({ showModal: false }));
              navigate({ pathname: "/" }, { state: { initial_deeplink: true } });
              if (cookies.STATE === "account_already_exist") {
                removeCookies("STATE", { path: "/" });
              }

              removeCookies("OFFER", { path: "/subscriptionplus" });
              removeCookies("PARTNER", { path: "/subscriptionplus" });
            }}
          />
        ),
      }),
    );
  }, [
    selectedOfferToDeeplinkWithName?.marketplaceInfo?.notEligible,
    cookies.STATE,
    dispatch,
    t,
    navigate,
    removeCookies,
  ]);

  const handleNotEligible = useCallback(
    (offer: Offer) => {
      const notEligible = offer?.marketplaceInfo?.notEligible?.[cookies.STATE as NotEligibleState];
      const isModal =
        cookies.STATE === "account_already_exist" && offerByName === null
          ? notEligible?.showTrialFailedModal // add this in gb once trial failed trial suppress by ER hence each time data will come from ctb json
          : notEligible?.isModal;

      if (isModal) {
        // Do this if it's a SA or HB
        openNotEligibleModal();
      } else {
        // Do this if it's a trial
        navigate({ pathname: `/offer/${offer.name}/not-eligible`, search }, { state: { initial_deeplink: true } });
      }
    },
    [cookies.STATE, openNotEligibleModal, navigate, search, offerByName],
  );

  useEffect(() => {
    if (state?.skipDeeplinkRedirect) {
      return;
    }

    if (offers.offersList.length > 0 && !cookies.STATE && cookies.OFFER && selectedOfferToDeeplinkWithName) {
      navigate(
        { pathname: `/offer/${selectedOfferToDeeplinkWithName.name}`, search },
        { state: { initial_deeplink: true } },
      );

      removeCookies("PARTNER", { path: "/subscriptionplus" });
      removeCookies("OFFER", { path: "/subscriptionplus" });
    } else if (
      offers.offersList.length > 0 &&
      !cookies.STATE &&
      !cookies.OFFER &&
      cookies.PARTNER &&
      selectedOfferToDeeplinkWithPartner
    ) {
      navigate(
        { pathname: `/offer/${selectedOfferToDeeplinkWithPartner.name}`, search },
        { state: { initial_deeplink: true } },
      );

      removeCookies("PARTNER", { path: "/subscriptionplus" });
      removeCookies("OFFER", { path: "/subscriptionplus" });
    } else if (
      offers.offersList.length > 0 &&
      !cookies.STATE &&
      !cookies.OFFER &&
      !cookies.PARTNER &&
      hasDeeplinkToken &&
      selectedOfferToDeeplinkWithToken &&
      !hasRedirectedWithToken.current
    ) {
      // The tk stays in the URL/sessionStorage, so only redirect once per page load
      // to avoid a navigation loop (Safari throws after 100 pushState calls per 30s)
      hasRedirectedWithToken.current = true;
      navigate(
        { pathname: `/offer/${selectedOfferToDeeplinkWithToken.name}`, search },
        { state: { initial_deeplink: true } },
      );
    }
  }, [
    selectedOfferToDeeplinkWithName,
    selectedOfferToDeeplinkWithPartner,
    selectedOfferToDeeplinkWithToken,
    hasDeeplinkToken,
    navigate,
    removeCookies,
    offers.offersList.length,
    cookies.STATE,
    cookies.OFFER,
    cookies.PARTNER,
    cookies.ACTIVATIONCODE,
    dispatch,
    search,
    state,
  ]);

  useEffect(() => {
    if (offers.offersList.length === 0) return;

    const canHaveTrial = ![
      "not_eligible_for_trial",
      "account_already_exist",
      "access_denied",
      "youtube_partner_error",
    ].includes(cookies?.STATE);

    if (canHaveTrial) return;

    if (cookies.OFFER && selectedOfferToDeeplinkWithName) {
      handleNotEligible(selectedOfferToDeeplinkWithName);
    } else if (cookies.PARTNER && selectedOfferToDeeplinkWithPartner) {
      handleNotEligible(selectedOfferToDeeplinkWithPartner);
    }
  }, [
    handleNotEligible,
    selectedOfferToDeeplinkWithName,
    selectedOfferToDeeplinkWithPartner,
    dispatch,
    navigate,
    offers.offersList.length,
    cookies.STATE,
    cookies.OFFER,
    cookies.PARTNER,
  ]);

  return;
};

export default useDeeplink;
